import React from "react";
import { ClipboardList, Plus, Trash2 } from "lucide-react";
import DrugSearchInput from "@/components/DrugSearchInput";

/**
 * Anamnesi strutturata a righe con toggle No/Sì (+dettaglio) — additiva rispetto
 * all'anamnesi testuale. Salvata in visita come `anamnesi_structured` (retro-compatibile:
 * i vecchi documenti senza il campo mostrano semplicemente toggle non impostati).
 */
export const ANAMNESI_ROWS = {
  generale: [
    { k: "allergie", label: "Allergie", type: "yn", detailPh: "A cosa è allergico (farmaci, colliri, altro)…" },
    { k: "hbv", label: "Epatite B (HBV)", type: "ynu" },
    { k: "hcv", label: "Epatite C (HCV)", type: "ynu" },
    { k: "hiv", label: "HIV", type: "ynu" },
    { k: "hsv_hzv", label: "HSV / HZV o altre infezioni rilevanti", type: "ynu" },
    { k: "anticoagulanti", label: "Anticoagulanti", type: "yn", detailPh: "Farmaco…" },
    { k: "antiaggreganti", label: "Cardioaspirina / antiaggreganti", type: "yn", detailPh: "Farmaco…" },
    { k: "ipertensione", label: "Ipertensione arteriosa", type: "yn" },
    { k: "diabete", label: "Diabete", type: "yn", sub: "diabete_controllato" },
    { k: "tiroide", label: "Patologie tiroidee", type: "yn" },
    { k: "cardiopatie", label: "Cardiopatie", type: "yn" },
    { k: "altre_patologie", label: "Altre patologie importanti", type: "yn" },
  ],
  oculare: [
    { k: "patologie_oculari_permanenti", label: "Patologie permanenti oculari", type: "yn" },
    { k: "patologie_oculari_pregresse", label: "Patologie oculari pregresse / attuali", type: "yn" },
    // "occhiali" e "lac" rimossi dalla UI (duplicati della sezione Refrazione/Lenti in uso).
    // I dati legacy in anamnesi_structured.occhiali/lac restano nel DB, mai cancellati.
  ],
};

const ESAMI_TIPOLOGIE = ["OCT", "Campo visivo", "Topografia", "Pachimetria", "Biometria", "Tonometria", "Fundus", "Retinografia", "Fluorangiografia", "Aberrometria", "Autoref", "Altro"];

/** Deriva toggle Sì/No dall'anamnesi testuale (dettatura IA) — deterministico, mai inventa. */
export function deriveAnamnesiStructured(text, prev = {}) {
  const t = (text || "").toLowerCase();
  if (!t.trim()) return prev;
  const out = JSON.parse(JSON.stringify(prev || {}));
  const set = (k, v, note) => {
    out[k] = { ...(out[k] || {}), v, ...(note ? { note } : {}) };
  };
  const grab = (re) => { const m = t.match(re); return m ? m[0] : ""; };
  if (/nessuna allergia|non allergic|nega allergie|allergie[:\s]*(no|negat)/.test(t)) set("allergie", "no");
  else if (/allergi[ae]/.test(t)) set("allergie", "si", grab(/allergi[ae][^.;\n]{0,80}/));
  if (/diabete|diabetic/.test(t)) {
    set("diabete", "si", grab(/diabet[^.;\n]{0,60}/));
    if (/ben controllat|compensat/.test(t)) out.diabete_controllato = { v: "si" };
    else if (/scompensat|mal controllat|non controllat/.test(t)) out.diabete_controllato = { v: "no" };
  } else if (/nega diabete|non diabetic/.test(t)) set("diabete", "no");
  if (/ipertension|ipertes/.test(t)) set("ipertensione", "si", grab(/ipertens[^.;\n]{0,60}/));
  if (/anticoagulant|coumadin|warfarin|eliquis|xarelto|pradaxa/.test(t)) set("anticoagulanti", "si", grab(/(anticoagulant|coumadin|warfarin|eliquis|xarelto|pradaxa)[^.;\n]{0,50}/));
  if (/cardioaspirin|cardiaspirin|antiaggregant|aspirinetta/.test(t)) set("antiaggreganti", "si", grab(/(cardioaspirin|cardiaspirin|antiaggregant|aspirinetta)[^.;\n]{0,50}/));
  if (/tiroid|ipotiroid|ipertiroid|hashimoto|basedow/.test(t)) set("tiroide", "si", grab(/(tiroid|ipotiroid|ipertiroid|hashimoto|basedow)[^.;\n]{0,60}/));
  if (/cardiopat|infarto|fibrillazione|scompenso cardiaco/.test(t)) set("cardiopatie", "si", grab(/(cardiopat|infarto|fibrillazione|scompenso cardiaco)[^.;\n]{0,60}/));
  if (/epatite b|hbv/.test(t)) set("hbv", /negativ/.test(t) ? "no" : "si");
  if (/epatite c|hcv/.test(t)) set("hcv", /negativ/.test(t) ? "no" : "si");
  if (/\bhiv\b/.test(t)) set("hiv", /negativ/.test(t) ? "no" : "si");
  if (/herpes|hsv|hzv|zoster/.test(t)) set("hsv_hzv", "si", grab(/(herpes|hsv|hzv|zoster)[^.;\n]{0,60}/));
  if (/porta (gli )?occhiali|correzione a tempiale|occhiali da/.test(t)) set("occhiali", "si");
  if (/lenti a contatto|\blac\b/.test(t)) set("lac", /non (usa|porta)/.test(t) ? "no" : "si");
  if (/intervento di|operat[oa] di|pregressa chirurgia|facoemulsificazione|cheratoplastica/.test(t)) {
    out.interventi_oculari = { ...(out.interventi_oculari || {}), v: "si" };
  }
  return out;
}

function Toggle3({ value, onChange, withUnknown, readOnly, testid }) {
  const opts = withUnknown ? [["no", "No"], ["si", "Sì"], ["nn", "Non noto"]] : [["no", "No"], ["si", "Sì"]];
  return (
    <div className="flex gap-1" data-testid={testid}>
      {opts.map(([v, lbl]) => {
        const on = value === v;
        const color = v === "si" ? "bg-brand-orange border-brand-orange" : v === "no" ? "bg-emerald-600 border-emerald-600" : "bg-slate-500 border-slate-500";
        return (
          <button key={v} type="button" disabled={readOnly}
            data-testid={`${testid}-${v}`}
            onClick={() => onChange(on ? null : v)}
            className={`px-2.5 py-1 rounded-full border-2 text-[11px] font-bold transition disabled:opacity-50 ${on ? `${color} text-white` : "bg-white border-[#E2E8F0] text-slate-500 hover:border-slate-400"}`}>
            {lbl}
          </button>
        );
      })}
    </div>
  );
}

function MultiRows({ items, setItems, columns, readOnly, testid, addLabel }) {
  // Updater sempre funzionali: evitano lost-update dei caratteri con re-render rapidi.
  const add = () => setItems((prev) => [...(prev || []), Object.fromEntries(columns.map((c) => [c.k, ""]))]);
  const upd = (i, k, val) => setItems((prev) => prev.map((it, j) => (j === i ? { ...it, [k]: val } : it)));
  const del = (i) => setItems((prev) => prev.filter((_, j) => j !== i));
  return (
    <div className="mt-1.5 space-y-1.5" data-testid={testid}>
      {(items || []).map((it, i) => (
        <div key={i} className="flex flex-wrap items-center gap-1.5">
          {columns.map((c) => c.options ? (
            <select key={c.k} disabled={readOnly} value={it[c.k] || ""} onChange={(e) => upd(i, c.k, e.target.value)}
              className="px-2 py-1 rounded border border-[#E2E8F0] text-xs bg-white" data-testid={`${testid}-${i}-${c.k}`}>
              <option value="">{c.ph}</option>
              {c.options.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          ) : (
            <input key={c.k} disabled={readOnly} value={it[c.k] || ""} onChange={(e) => upd(i, c.k, e.target.value)}
              placeholder={c.ph} className={`px-2 py-1 rounded border border-[#E2E8F0] text-xs ${c.w || "w-32"}`} data-testid={`${testid}-${i}-${c.k}`} />
          ))}
          {!readOnly && <button onClick={() => del(i)} className="text-slate-400 hover:text-red-600"><Trash2 size={13} /></button>}
        </div>
      ))}
      {!readOnly && (
        <button type="button" onClick={add} data-testid={`${testid}-add`}
          className="text-[11px] font-bold text-brand-azure hover:underline flex items-center gap-1"><Plus size={11} /> {addLabel}</button>
      )}
    </div>
  );
}

// Righe terapia oculare con collegamento al prontuario (item retro-compatibile:
// {farmaco, occhio, frequenza, note} + drug_ref strutturato quando selezionato dal prontuario).
// setItems accetta SOLO updater funzionali (vedi setRowItems) per evitare lost-update.
function TerapiaOculareRows({ items, setItems, readOnly, testid }) {
  const add = () => setItems((prev) => [...(prev || []), { farmaco: "", occhio: "", frequenza: "", note: "" }]);
  const upd = (i, patch) => setItems((prev) => prev.map((it, j) => (j === i ? { ...it, ...patch } : it)));
  const del = (i) => setItems((prev) => prev.filter((_, j) => j !== i));
  const pick = (i, d) => {
    const freqDefault = d.default_posology?.frequency_per_day
      ? `${d.default_posology.frequency_per_day} volte/die`
      : "";
    setItems((prev) => prev.map((it, j) => (j === i ? {
      ...it,
      farmaco: d.brand,
      frequenza: it.frequenza || freqDefault,
      drug_ref: {
        atc: d.atc, brand: d.brand, principle: d.principle, form: d.form,
        category: d.category, source: d.source || "", verified: !!d.verified,
        default_posology: d.default_posology || null,
      },
    } : it)));
  };
  return (
    <div className="mt-1.5 space-y-1.5" data-testid={testid}>
      {(items || []).map((it, i) => (
        <div key={i} className="flex flex-wrap items-center gap-1.5">
          <DrugSearchInput
            value={it.farmaco || ""}
            linked={it.drug_ref || null}
            readOnly={readOnly}
            testid={`${testid}-${i}-farmaco`}
            placeholder="Farmaco/collirio (nome o principio attivo)…"
            onText={(txt) => upd(i, { farmaco: txt, drug_ref: undefined })}
            onSelect={(d) => pick(i, d)}
            onUnlink={() => upd(i, { drug_ref: undefined })}
          />
          <select disabled={readOnly} value={it.occhio || ""} onChange={(e) => upd(i, { occhio: e.target.value })}
            className="px-2 py-1 rounded border border-[#E2E8F0] text-xs bg-white" data-testid={`${testid}-${i}-occhio`}>
            <option value="">Occhio</option>
            {["OD", "OS", "OU"].map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
          <input disabled={readOnly} value={it.frequenza || ""} onChange={(e) => upd(i, { frequenza: e.target.value })}
            placeholder="Frequenza/posologia" className="px-2 py-1 rounded border border-[#E2E8F0] text-xs w-32" data-testid={`${testid}-${i}-frequenza`} />
          <input disabled={readOnly} value={it.note || ""} onChange={(e) => upd(i, { note: e.target.value })}
            placeholder="Note" className="px-2 py-1 rounded border border-[#E2E8F0] text-xs w-28" data-testid={`${testid}-${i}-note`} />
          {!readOnly && <button onClick={() => del(i)} className="text-slate-400 hover:text-red-600"><Trash2 size={13} /></button>}
        </div>
      ))}
      {!readOnly && (
        <button type="button" onClick={add} data-testid={`${testid}-add`}
          className="text-[11px] font-bold text-brand-azure hover:underline flex items-center gap-1"><Plus size={11} /> Aggiungi collirio/farmaco</button>
      )}
    </div>
  );
}

// Componente a livello di modulo (NON dentro il render del parent): definirlo inline
// creava un nuovo tipo React a ogni render → remount degli input → focus perso a ogni tasto.
function AnamRow({ row, data, setRow, onChange, readOnly }) {
  const rec = data[row.k] || {};
  const isSi = rec.v === "si";
  // Retro-compatibilità: dati legacy (allergie farmaci/colliri, malattie infettive) mostrati
  // in modo non invasivo sotto il campo unico Allergie, mai come blocchi separati.
  const legacyNotes = row.k === "allergie"
    ? [
        data.allergie_farmaci?.v === "si" && `Allergie farmaci/colliri (dato precedente): sì${data.allergie_farmaci.note ? ` — ${data.allergie_farmaci.note}` : ""}`,
        data.malattie_infettive?.v === "si" && `Malattie infettive (dato precedente): sì${data.malattie_infettive.note ? ` — ${data.malattie_infettive.note}` : ""}`,
      ].filter(Boolean)
    : [];
  return (
    <div className="py-1.5 border-b border-slate-100 last:border-0">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <span className="text-sm text-slate-700 font-medium">{row.label}</span>
        <Toggle3 value={rec.v || null} onChange={(v) => setRow(row.k, { v })} withUnknown={row.type === "ynu"} readOnly={readOnly} testid={`anam-${row.k}`} />
      </div>
      {legacyNotes.length > 0 && (
        <div className="mt-1 text-[10px] text-slate-500 bg-slate-50 border border-slate-200 rounded px-2 py-1" data-testid="anam-allergie-legacy">
          {legacyNotes.map((n, i) => <div key={i}>{n}</div>)}
        </div>
      )}
      {isSi && !row.noDetail && (
        <input disabled={readOnly} value={rec.note || ""} onChange={(e) => setRow(row.k, { note: e.target.value })}
          placeholder={row.detailPh || "Dettaglio…"} data-testid={`anam-${row.k}-note`}
          className="mt-1 w-full px-2.5 py-1.5 rounded border border-amber-200 bg-amber-50/50 text-xs focus:outline-none focus:border-brand-azure" />
      )}
      {row.sub === "diabete_controllato" && isSi && (
        <div className="mt-1.5 flex items-center justify-between gap-3 pl-3 border-l-2 border-amber-300">
          <span className="text-xs text-slate-600">Ben controllato?</span>
          <Toggle3 value={(data.diabete_controllato || {}).v || null} onChange={(v) => setRow("diabete_controllato", { v })} withUnknown readOnly={readOnly} testid="anam-diabete-controllato" />
        </div>
      )}
    </div>
  );
}

export default function AnamnesiStructured({ value, onChange, readOnly }) {
  const data = value || {};
  // Updater funzionali: evitano lost-update dei caratteri (Visita ri-renderizza a ogni keystroke).
  const setRow = (k, patch) => onChange((prev) => {
    const p = prev || {};
    return { ...p, [k]: { ...(p[k] || {}), ...patch } };
  });
  const setRowItems = (k) => (updater) => onChange((prev) => {
    const p = prev || {};
    const cur = (p[k] || {}).items || [];
    const items = typeof updater === "function" ? updater(cur) : updater;
    return { ...p, [k]: { ...(p[k] || {}), items } };
  });

  const interventi = (data.interventi_oculari || {});
  const terapia = (data.terapia_oculare || {});
  const esami = (data.esami_eseguiti || {});

  return (
    <div data-testid="anamnesi-structured">
      <div className="flex items-center gap-2 mb-2">
        <ClipboardList size={15} className="text-brand-azure" />
        <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Anamnesi strutturata</span>
        <span className="text-[10px] text-slate-400">l&apos;IA può autosbarrare Sì/No dalla dettatura — verifica sempre</span>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="border border-[#E2E8F0] rounded-lg p-3">
          <div className="text-[11px] font-extrabold uppercase tracking-wider text-brand-navy mb-1">Anamnesi generale</div>
          {ANAMNESI_ROWS.generale.map((row) => <AnamRow key={row.k} row={row} data={data} setRow={setRow} onChange={onChange} readOnly={readOnly} />)}
          <div className="pt-2">
            <label className="text-xs text-slate-600 font-medium">Farmaci sistemici in uso</label>
            <textarea disabled={readOnly} rows={2} value={(data.farmaci_sistemici || {}).note || ""}
              onChange={(e) => setRow("farmaci_sistemici", { note: e.target.value })}
              placeholder="Elenco farmaci sistemici…" data-testid="anam-farmaci-sistemici"
              className="mt-1 w-full px-2.5 py-1.5 rounded border border-[#E2E8F0] text-xs resize-none" />
          </div>
        </div>
        <div className="border border-[#E2E8F0] rounded-lg p-3">
          <div className="text-[11px] font-extrabold uppercase tracking-wider text-brand-navy mb-1">Anamnesi oculare</div>
          {ANAMNESI_ROWS.oculare.map((row) => <AnamRow key={row.k} row={row} data={data} setRow={setRow} onChange={onChange} readOnly={readOnly} />)}

          <div className="py-1.5 border-b border-slate-100">
            <div className="flex items-center justify-between gap-3">
              <span className="text-sm text-slate-700 font-medium">Interventi chirurgici oculari pregressi</span>
              <Toggle3 value={interventi.v || null} onChange={(v) => setRow("interventi_oculari", { v })} readOnly={readOnly} testid="anam-interventi" />
            </div>
            {interventi.v === "si" && (
              <MultiRows items={interventi.items || []} setItems={setRowItems("interventi_oculari")} readOnly={readOnly} testid="anam-interventi-rows" addLabel="Aggiungi intervento"
                columns={[{ k: "tipo", ph: "Tipo intervento", w: "w-40" }, { k: "occhio", ph: "Occhio", options: ["OD", "OS", "OU"] }, { k: "anno", ph: "Anno/data", w: "w-24" }, { k: "struttura", ph: "Struttura/medico", w: "w-36" }]} />
            )}
          </div>

          <div className="py-1.5 border-b border-slate-100">
            <span className="text-sm text-slate-700 font-medium">Esami oculistici già eseguiti</span>
            <MultiRows items={esami.items || []} setItems={setRowItems("esami_eseguiti")} readOnly={readOnly} testid="anam-esami-rows" addLabel="Aggiungi esame"
              columns={[{ k: "data", ph: "Data", w: "w-24" }, { k: "tipo", ph: "Tipologia", options: ESAMI_TIPOLOGIE }, { k: "esito", ph: "Esito/note", w: "w-40" }, { k: "struttura", ph: "Struttura", w: "w-28" }]} />
          </div>

          <div className="py-1.5">
            <div className="flex items-center justify-between gap-3">
              <span className="text-sm text-slate-700 font-medium">Terapia oculare in corso</span>
              <Toggle3 value={terapia.v || null} onChange={(v) => setRow("terapia_oculare", { v })} readOnly={readOnly} testid="anam-terapia-oculare" />
            </div>
            {terapia.v === "si" && (
              <>
                <TerapiaOculareRows items={terapia.items || []} setItems={setRowItems("terapia_oculare")} readOnly={readOnly} testid="anam-terapia-rows" />
                <div className="mt-1 text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                  Terapia riferita/in corso: cerca nel prontuario (nome commerciale o principio attivo) per collegare il farmaco — verrà proposta nella sezione Diagnosi e Terapia in forma strutturata. Il medico conferma, modifica o rimuove prima di prescrivere.
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
